import { Link } from "react-router-dom";
import { Shield, Camera, MessageCircleMore, Star, ArrowRight, Monitor, Smartphone, Zap, Activity, Radio, Target } from "lucide-react";
import { BrandWord } from "@/components/BrandWord";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0E14] warroom-grid warroom-scanlines text-[#E2E8F0]">
      {/* Navigation - Mission Control Header */}
      <nav className="fixed top-0 w-full z-50 bg-[#0A0E14]/90 backdrop-blur-sm border-b border-[#1E293B]" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo192.png" alt="ShiftQuote" className="w-8 h-8 object-contain shrink-0" />
            <BrandWord className="font-heading text-xl font-black tracking-tight" />
            <span className="hidden sm:inline-flex status-online ml-3">OPERATIONAL</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-xs font-mono font-medium text-[#64748B] hover:text-[#2563EB] transition-colors uppercase tracking-wider">Login</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section - War Room Briefing */}
      <section className="relative pt-28 pb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            {/* Status Bar */}
            <div className="flex items-center gap-4 mb-8 font-mono text-[10px] tracking-wider uppercase text-[#64748B]">
              <span className="flex items-center gap-1.5">
                <Activity className="w-3 h-3 text-emerald-400" />
                <span className="text-emerald-400">SYS:ACTIVE</span>
              </span>
              <span className="text-[#334155]">|</span>
              <span>SECTOR: AUTO-REPAIR</span>
              <span className="text-[#334155]">|</span>
              <span>PROTOCOL: PRIVATE BY DESIGN</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-6">
              <span className="text-[#E2E8F0]">FIX YOUR CAR</span><br />
              <span className="text-[#2563EB]">WITHOUT THE GUESSWORK.</span>
            </h1>

            <p className="text-base md:text-lg text-[#94A3B8] max-w-2xl mb-4 leading-relaxed font-body">
              Private by design. Your name stays hidden until you accept a quote. Real-time intel from local shops. You control the engagement.
            </p>
            <p className="text-sm text-[#64748B] font-mono uppercase tracking-wider mb-8">By a driver, for drivers.</p>

            {/* CTA Grid */}
            <div className="flex flex-wrap gap-3">
              <Link to="/login?mode=register&role=owner" className="flex items-center gap-3 bg-[#2563EB] border border-[#3B82F6] text-white px-6 py-3.5 font-bold text-sm uppercase tracking-wider hover:bg-[#3B82F6] hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all">
                <Target className="w-4 h-4" /> Deploy as Driver <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login?mode=register&role=shop" className="flex items-center gap-3 bg-[#0F172A] border border-[#334155] text-[#94A3B8] px-6 py-3.5 font-bold text-sm uppercase tracking-wider hover:border-[#64748B] hover:text-white transition-all">
                <Monitor className="w-4 h-4" /> Deploy as Shop
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative grid corner accents */}
        <div className="absolute top-24 right-8 hidden lg:block">
          <div className="w-48 h-48 border border-[#1E293B] relative">
            <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#2563EB]" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-[#2563EB]" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-[#2563EB]" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#2563EB]" />
            <div className="absolute inset-4 flex flex-col justify-center items-center gap-2 font-mono text-[10px] text-[#64748B] uppercase">
              <Radio className="w-5 h-5 text-[#2563EB] mb-1" />
              <span>RADIUS SCAN</span>
              <span className="text-[#2563EB] text-lg font-bold">25 MI</span>
              <span>SHOPS IN RANGE</span>
            </div>
          </div>
        </div>
      </section>

      {/* Intel Grid - The Dual-Purpose Layout */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-[#1E293B]">
        <div className="label-eyebrow mb-8">Deployment Options</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Driver Panel */}
          <div className="card-base p-6 panel-accent relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#2563EB]/10 border border-[#2563EB]/30 flex items-center justify-center text-[#2563EB]">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold tracking-tight text-[#E2E8F0]">DRIVER OPS</h3>
                <span className="font-mono text-[10px] text-[#64748B] uppercase">Mobile Command</span>
              </div>
            </div>
            <p className="text-sm text-[#94A3B8] mb-5 leading-relaxed">Post your issue with photo/video intel. Your name stays hidden until you accept a quote. Only vetted shops can send private, non-binding quotes.</p>
            <ul className="space-y-2.5 text-xs font-mono text-[#64748B]">
              <li className="flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-emerald-400" /> <span className="text-[#94A3B8]">NAME: HIDDEN UNTIL YOU ACCEPT</span></li>
              <li className="flex items-center gap-2"><Camera className="w-3.5 h-3.5 text-emerald-400" /> <span className="text-[#94A3B8]">MEDIA: PHOTO + VIDEO</span></li>
              <li className="flex items-center gap-2"><MessageCircleMore className="w-3.5 h-3.5 text-emerald-400" /> <span className="text-[#94A3B8]">COMMS: PRIVATE 1-ON-1</span></li>
            </ul>
          </div>

          {/* Shop Panel */}
          <div className="card-base p-6 panel-accent relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#2563EB]/10 border border-[#2563EB]/30 flex items-center justify-center text-[#2563EB]">
                <Monitor className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold tracking-tight text-[#E2E8F0]">SHOP OPS</h3>
                <span className="font-mono text-[10px] text-[#64748B] uppercase">Desktop Command Center</span>
              </div>
            </div>
            <p className="text-sm text-[#94A3B8] mb-5 leading-relaxed">Professional portal to manage your lead pipeline. View detailed media, communicate with local owners, deploy precise quotes from HQ.</p>
            <ul className="space-y-2.5 text-xs font-mono text-[#64748B]">
              <li className="flex items-center gap-2"><Monitor className="w-3.5 h-3.5 text-[#2563EB]" /> <span className="text-[#94A3B8]">INTERFACE: DESKTOP PORTAL</span></li>
              <li className="flex items-center gap-2"><Target className="w-3.5 h-3.5 text-[#2563EB]" /> <span className="text-[#94A3B8]">INTEL: LOCAL LEAD RADAR</span></li>
              <li className="flex items-center gap-2"><Star className="w-3.5 h-3.5 text-[#2563EB]" /> <span className="text-[#94A3B8]">RANK: REPUTATION SYSTEM</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="max-w-7xl mx-auto px-6 py-8 border-t border-[#1E293B]">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="font-mono text-2xl font-bold text-[#2563EB]">$0</div>
            <div className="font-mono text-[10px] text-[#64748B] uppercase mt-1">Commission Fee</div>
          </div>
          <div className="text-center border-x border-[#1E293B]">
            <div className="font-mono text-2xl font-bold text-[#E2E8F0]">PRIVATE</div>
            <div className="font-mono text-[10px] text-[#64748B] uppercase mt-1">By Design</div>
          </div>
          <div className="text-center">
            <div className="font-mono text-2xl font-bold text-emerald-400">LIVE</div>
            <div className="font-mono text-[10px] text-[#64748B] uppercase mt-1">Real-Time Quotes</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex flex-col items-center sm:items-start gap-1">
            <BrandWord className="font-heading text-lg font-black" />
            <span className="font-mono text-[10px] text-[#64748B] uppercase tracking-wider">By a driver, for drivers.</span>
          </div>
          <div className="flex items-center gap-6 text-[10px] font-mono font-medium tracking-wider text-[#475569] uppercase">
            <Link to="/privacy" className="hover:text-[#2563EB] transition-colors">Privacy</Link>
            <span className="text-[#1E293B]">|</span>
            <Link to="/terms" className="hover:text-[#2563EB] transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
